/* eslint-disable @typescript-eslint/no-inferrable-types */
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ActionAllowed, LibraryItem } from 'src/app/services/models';
import { SideBarService } from 'src/app/services/side-bar.service';
import { DocumentLibraryService } from 'src/app/services/document-library.service';
import { ErrorService } from 'src/app/services/error.service';
import { ConfirmationDialogService } from '../confirmation-dialog/confirmation-dialog.service';
import { environment } from 'src/environments/environment';
import { getFileNameWithOutExtensionFromUrl, getFileExtension, newId, getScrollParent } from '../../services/utils'
import { IBasePermissions, PermissionKind } from '@pnp/sp/security';
import { sp } from '@pnp/sp/presets/all';
import { ExtensionMappings } from 'src/app/services/constants';
import { ActivatedRoute, Router } from '@angular/router';

export interface DocumentProperty {
  caption: string,
  name: string,
  type: string,
  isMandatory: boolean
}

@Component({
  selector: 'app-documents-list',
  templateUrl: './documents-list.component.html'
})
export class DocumentsListComponent implements OnInit {

  @Input() documentProperties: DocumentProperty[] = [
    {
      caption: 'Collateral Type',
      name: 'CollateralTypeId',
      type: 'CollateralType',
      isMandatory: true
    },
    {
      caption: 'Keywords',
      name: 'HTAKeywords',
      type: 'Keywords',
      isMandatory: false
    }
  ];
  @Input() isLinkAllowed = false;
  @Input() libraryName: string;
  @Input() title = 'Guidance Materials';
  @Input() uploadDocumentTitle = 'Upload Guidance';
  @Input() permissions: ActionAllowed;
  @Input() mode: 'List' | 'Carousel' = 'List';
  @Input() fileNameTreatment: boolean = false;
  @Output() seeAllChange = new EventEmitter<boolean>();

  private _seeAll = false;
  get seeAll(): boolean {
    return this._seeAll;
  }
  @Input()
  set seeAll(value: boolean) {
    if (value !== this._seeAll) {
      this._seeAll = value;
      this.seeAllChange.emit(value);
    }
  }
  loadPageData: boolean = true;
  allGuidanceDocuments: LibraryItem[];
  closeResult = '';
  isLoading = false;
  addButtonVisible = true;
  guid = newId();
  listPermissions: IBasePermissions;
  canAddDocument = false;
  elementId = newId();

  id = "";

  private _libraryItems: LibraryItem[];
  get libraryItems(): LibraryItem[] {
    return this._libraryItems;
  }
  set libraryItems(items: LibraryItem[]) {
    if (items === this._libraryItems) return;
    this._libraryItems = items;
    if (items) {
      items.forEach(it => {
        if (!it.ThumbnailSafeUrl) {
          this.documentLibraryService.getThumbnailSafeUrl(it.FileRef).then(safeUrl => {
            it.ThumbnailSafeUrl = safeUrl;
          });
        }
      })
    }
  }

  responsiveOptions;

  constructor(
    public sidebar: SideBarService,
    private documentLibraryService: DocumentLibraryService,
    private errorService: ErrorService,
    private confirmationDialogService: ConfirmationDialogService,
    private route: ActivatedRoute,
  ) {

    this.responsiveOptions = [
      {
        breakpoint: '1020px',
        numVisible: 4,
        numScroll: 4
      },
      {
        breakpoint: '988px',
        numVisible: 3,
        numScroll: 3
      },
      {
        breakpoint: '756px',
        numVisible: 2,
        numScroll: 2
      },
      {
        breakpoint: '525px',
        numVisible: 1,
        numScroll: 1
      }
    ];
    this.route.paramMap.subscribe(params => {
      this.id = params.get('id');
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      if (this.permissions) {
        if (this.libraryName != 'AccessTrends-Resources') {
          this.addButtonVisible = this.permissions.create;
        }
        this.canAddDocument = this.permissions.create && await this.documentLibraryService.currentUserHasPermission(this.libraryName, PermissionKind.AddListItems);

      }
      else {
        this.canAddDocument = await this.documentLibraryService.currentUserHasPermission(this.libraryName, PermissionKind.AddListItems);
      }
    }
    catch (err) {
      this.errorService.show('Failed to get permission information', err);
    }
    if (this.libraryName != 'AccessTrends-Resources') {
      await this.loadLibraryItems();
    }
    else {
      await this.loadLibraryItemsById(this.id);
    }

    const listfilterSeesionObj = JSON.parse(sessionStorage.getItem('ListFilterSessionObj'));
    if(listfilterSeesionObj){
      sessionStorage.removeItem("ListFilterSessionObj");
    }

  }

  async loadLibraryItems(): Promise<void> {
    try {
      this.allGuidanceDocuments = await this.documentLibraryService.getFileItems(this.libraryName, false);
      this.allGuidanceDocuments.forEach(x => {
        x.FileName = getFileNameWithOutExtensionFromUrl(x.FileRef);
        if (x.Title) {
          x.Title = getFileNameWithOutExtensionFromUrl(x.Title);
        }
        if (x ?.FileRef !== undefined && x.FileRef !== '' && x.FileRef.includes('#')) {
          x.FileRef = x.FileRef.replace(/#/g, '%23');
        }
      });
      this.setLibraryItems();
    }
    catch (error) {
      this.errorService.show(`Failed to get files from '${this.libraryName}' document library`, error);
    }
  }

  async loadLibraryItemsById(id): Promise<void> {
    try {
      this.allGuidanceDocuments = await this.documentLibraryService.getFileItemsById(this.libraryName, false, id);
      this.allGuidanceDocuments.forEach(x => {
        x.FileName = getFileNameWithOutExtensionFromUrl(x.FileRef);
        if (x.Title) {
          x.Title = getFileNameWithOutExtensionFromUrl(x.Title);
        }
        if (x ?.FileRef !== undefined && x.FileRef !== '' && x.FileRef.includes('#')) {
          x.FileRef = x.FileRef.replace(/#/g, '%23');
        }
      });
      this.setLibraryItems();

    }
    catch (error) {
      this.errorService.show(`Failed to get files from '${this.libraryName}' document library`, error);
    }
  }

  private setLibraryItems() {
    if (!this._seeAll) {
      this.libraryItems = this.allGuidanceDocuments.slice(0, 8);
    }
    else {
      this.libraryItems = this.allGuidanceDocuments;
    }
    if (this.libraryName == 'AccessTrends-Resources') {
      if (this.libraryItems ?.length >= 1){
        this.loadPageData = true;
      }
      else {
        this.loadPageData = false;
      }
    }
  }

  toggleSeeAll(): void {
    this.seeAll = !this.seeAll;
    this.addButtonVisible = !this._seeAll;
    this.setLibraryItems();
    this.scrollToHome();
  }

  scrollToHome(): void {
    const el = document.getElementById(this.elementId);
    const scrollParent = getScrollParent(el);

    let url = window.location;
    if(url?.search.split("=")[1].indexOf('contracting%20guidance%20framework') < 0){
      if (scrollParent) scrollParent.scrollTo(0, 0);
    }
  }

  openNewDocumentSidebar(): void {
    if (this.canAddDocument === false) return;

    this.sidebar.show(this.guid, true);
  }

  async deleteAction(file: LibraryItem): Promise<void> {
    await this.documentLibraryService.deleteItem(this.libraryName, file);
    this.allGuidanceDocuments.splice(this.allGuidanceDocuments.findIndex(x => x === file), 1);
    this.setLibraryItems();


  }

  async deleteFile(file: LibraryItem): Promise<void> {
    if (this.canDeleteDocument(file) === false) return;

    let title:string="";
    if(file?.Title){
      title = file?.Title;
    }else{
      title = file?.FileName;
    }
    
    await this.confirmationDialogService.confirm(`Delete file ${title}`,
      'Deleting this item will permanently remove it from the AccessHub. Are you sure you want to proceed?',
      'Delete', 'Cancel', 'sm', () => this.deleteAction(file));
  }

  async refreshGuidanceDocuments(value: boolean): Promise<void> {

    if (this.libraryName != 'AccessTrends-Resources') {
      if (value) await this.loadLibraryItems();
    }
    else {
      if (value) await this.loadLibraryItemsById(this.id);
    }

  }

  getViewFileUrl(item: LibraryItem): string {
    return environment.serverUrl + item.FileRef + '?web=1';
  }

  getFileExtension(libraryItem: LibraryItem): string {
    if (libraryItem ?.FileRef){
      const extension = getFileExtension(libraryItem.FileRef);
      if (extension) return extension.toLowerCase();
      return '';
    }
    return '';
  }

  getFileIcon(libraryItem: LibraryItem): string {
    const extension = this.getFileExtension(libraryItem);
    const mapping = ExtensionMappings[extension];
    return mapping ? mapping : ExtensionMappings["generic"];
  }

  canDeleteDocument(item: LibraryItem): boolean {
    if (this.permissions) {
      return this.permissions.delete && sp.web.hasPermissions(item.EffectiveBasePermissions, PermissionKind.DeleteListItems);
    }
    return sp.web.hasPermissions(item.EffectiveBasePermissions, PermissionKind.DeleteListItems);
  }
}
